import React from 'react' // eslint-disable-line
import styled from 'styled-components'
import SearchIcon from '../../../../public/svg/search'
import { Typography } from '../../../Common'
import { IconClose } from '../../../Common/Icons'
import { SuggestedMenu } from '../../navbar.props'
import { Labels } from '../../../MenuLayout/MenuLayout.props'
import { useLocalLabels } from '@sentecacommerce-theme/lib'

export const Search = ({
  suggestedMenus,
  searchData,
  isInError,
  value,
  setValue,
  handleSearchBox,
}: {
  value: string
  searchData: any
  setValue: React.Dispatch<React.SetStateAction<string>>
  handleSearchBox: React.Dispatch<React.SetStateAction<boolean>>
  suggestedMenus?: SuggestedMenu[]
  isInError?: boolean
}) => {
  const labels = useLocalLabels<Labels>()

  const {
    inputRef,
    onInputChange,
    onSearchSubmit,
    completionTerm,
    handleKeyPress,
    clearResult,
  } = searchData

  const handleTextClear = () => {
    setValue('')
    clearResult()
  }

  return (
    <>
      <SearchForm>
        <Wrapper>
          <InputContainer suggested={completionTerm}>
            <SearchInput
              ref={inputRef}
              value={value}
              onChange={(e: any) => {
                onInputChange(e)
                setValue(e.target.value)
              }}
              onKeyDown={(e: any) => {
                handleKeyPress(e)
              }}
              placeholder={labels?.ariaLabelsAltsPlaceholders?.search}
              aria-label={labels?.ariaLabelsAltsPlaceholders?.search}
              onFocus={isInError ? undefined : () => handleSearchBox(true)}
            />

            <Value>
              <span>{value}</span>
              <span>
                {value && completionTerm.replace(value.toLowerCase(), '')}
              </span>
            </Value>
          </InputContainer>
          <ClearTextButton value={value} onClick={handleTextClear} />
          <ButtonContainer>
            <div
              onClick={(e: React.MouseEvent<HTMLElement>) => onSearchSubmit(e)}
            >
              <SearchIcon />
            </div>
          </ButtonContainer>
        </Wrapper>
      </SearchForm>
    </>
  )
}

const SearchForm = styled.form`
  background: transparent;
  position: absolute;
  left: 0;
  width: 100%;
  z-index: 100;
  transition: width 0.3s ease-out;
  height: 4rem;
  overflow: visible;

  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    height: 4.8rem;
    width: 100%;
    right: 0;
  }
`

const Value = styled.div`
  position: absolute;
  font-size: 1.6rem;
  line-height: 1.6rem;
  top: 1rem;
  left: 0;
  color: ${({ theme }) => theme.palette.silverTree};

  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    top: 1.3rem;
  }

  & > span:last-of-type {
    opacity: 0.3;
  }
`

const Wrapper = styled.label`
  display: block;
  border: 0.1rem solid ${({ theme }) => theme.palette.surfCrest};
  border-radius: 5rem;
  padding: 0.1rem;
  padding-left: 2rem;
  display: flex;
  background: transparent;
  align-items: center;
  height: 100%;
`

const InputContainer = styled.div.attrs({
  className: 'd-flex align-items-center',
})<{ suggested?: string }>`
  flex: 1;
  width: auto;
  position: relative;
  height: 100%;
`

const ButtonContainer = styled.div.attrs({
  className: 'ml-auto',
})`
  width: 2.8rem;
  height: 2.8rem;
  cursor: pointer;
  margin-right: 1.5rem;

  & svg {
    margin-top: 0.4rem;
    width: 2rem;
    height: 2rem;
    fill: ${({ theme }) => theme.palette.silverTree};
  }

  & div {
    display: flex;
    justify-content: flex-end;
  }
`

const SearchInput = styled(Typography).attrs({
  as: 'input',
  variant: 6,
  className: 'p-0 m-0',
})<{
  value: any
  hasValue?: boolean
}>`
  width: 100%;
  border: 0;
  outline: none;
  position: relative;
  text-transform: ${(props) => (props.hasValue ? 'lowercase' : 'none')};
  line-height: 1.6rem;
  z-index: 10;
  appearance: none;
  -webkit-appearance: none;
  background: 0 0;
  z-index: 2;
  color: rgba(0, 0, 0, 0);
  caret-color: ${({ theme }) => theme.palette.silverTree};

  &::placeholder {
    color: ${({ theme }) => theme.palette.silverTree};
  }
`

type ClearTextButtonType = {
  value?: string
}

const ClearTextButton = styled(IconClose).attrs({
  className: 'mr-4',
})<ClearTextButtonType>`
  display: ${({ value }) => value === '' && 'none'};
  width: 1.2rem;
  height: 1.2rem;

  color: ${({ theme }) => theme.palette.silverTree};

  cursor: pointer;
`
